import { useT } from "../lib/i18n.jsx";
import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api.js";
import { IconSearch } from "../components/icons.jsx";

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toISOString().slice(0, 10);
}

function statusTone(s) {
  if (s === "active") return "ok";
  if (s === "past_due" || s === "pending") return "warn";
  if (s === "canceled" || s === "expired") return "err";
  return "";
}

export default function AdminSubscriptionsPage() {
  const { t } = useT();
  const [subs, setSubs] = useState(null);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  async function load() {
    const r = await api("/admin/subscriptions");
    setSubs(r.subscriptions);
  }

  useEffect(() => {
    load().catch(() => {});
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  const filtered = useMemo(() => {
    if (!subs) return null;
    const f = filter.trim().toLowerCase();
    return subs.filter((s) => {
      if (statusFilter !== "all" && s.status !== statusFilter) return false;
      if (!f) return true;
      return (
        s.user_email?.toLowerCase().includes(f) ||
        s.user_name?.toLowerCase().includes(f) ||
        s.plan_name?.toLowerCase().includes(f) ||
        s.provider_sub_id?.toLowerCase().includes(f)
      );
    });
  }, [subs, filter, statusFilter]);

  const counts = useMemo(() => {
    if (!subs) return null;
    return {
      total: subs.length,
      active: subs.filter((s) => s.status === "active").length,
      past_due: subs.filter((s) => s.status === "past_due").length,
      canceled: subs.filter((s) => s.status === "canceled").length,
    };
  }, [subs]);

  if (!subs) return <div className="loading">Loading subscriptions</div>;

  return (
    <>
      <div className="ph">
        <div className="ph-title">
          <div className="eyebrow">Administration</div>
          <h1 className="h1">Subscriptions</h1>
        </div>
        {counts && (
          <div className="ph-meta">
            <div><strong>{counts.active}</strong> active</div>
            <div style={{ marginTop: 4 }}>{counts.total} total</div>
          </div>
        )}
      </div>

      {counts && (
        <div className="stats">
          <Stat label="Total" value={counts.total} />
          <Stat label="Active" value={counts.active} accent />
          <Stat label="Past due" value={counts.past_due} />
          <Stat label="Canceled" value={counts.canceled} />
        </div>
      )}

      <div className="toolbar">
        <div className="search">
          <IconSearch className="search-icon" size={14} />
          <input
            type="text"
            placeholder="search by user, plan, provider sub id…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            background: "var(--bg-elev)",
            border: "1px solid var(--line)",
            color: "var(--text)",
            padding: "9px 12px",
            borderRadius: "var(--r)",
            fontFamily: "var(--mono)",
            fontSize: 12,
            outline: "none",
          }}
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="past_due">Past due</option>
          <option value="pending">Pending</option>
          <option value="canceled">Canceled</option>
          <option value="expired">Expired</option>
        </select>
      </div>

      <div className="section">
        <div className="section-body" style={{ padding: 0 }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center" }}>
              <span className="serif" style={{ fontStyle: "italic", fontSize: 18, color: "var(--text-mute)" }}>
                no subscriptions match
              </span>
            </div>
          ) : (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Plan</th>
                  <th>Provider</th>
                  <th>Interval</th>
                  <th>Status</th>
                  <th>Period end</th>
                  <th>Created</th>
                  <th>Provider ID</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <div style={{ fontWeight: 500 }}>{s.user_name}</div>
                      <div className="mono" style={{ fontSize: 10.5, color: "var(--text-dim)" }}>
                        {s.user_email}
                      </div>
                    </td>
                    <td>{s.plan_name || <span className="dim">{s.plan_code}</span>}</td>
                    <td><span className="mono">{s.provider}</span></td>
                    <td><span className="chip">{s.interval_unit}</span></td>
                    <td>
                      <span className={"chip " + statusTone(s.status)}>{s.status}</span>
                      {s.cancel_at_period_end ? (
                        <span className="chip warn" style={{ marginLeft: 4, fontSize: 9 }}>
                          will end
                        </span>
                      ) : null}
                    </td>
                    <td className="mono" style={{ fontSize: 11 }}>
                      {fmtDate(s.current_period_end)}
                    </td>
                    <td className="mono" style={{ fontSize: 11, color: "var(--text-dim)" }}>
                      {fmtDate(s.created_at)}
                    </td>
                    <td className="mono" style={{ fontSize: 10.5, color: "var(--text-faint)", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.provider_sub_id || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="stat">
      <div className="stat-label">{label}</div>
      <div className={"stat-value" + (accent ? " acc" : "")}>{value}</div>
    </div>
  );
}
